'use client';

import './commitment-lists.scss';

import { useQueryState } from 'nuqs';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { PaymentFrequencyType } from '@sola/schema/dotcom/proxy-api/transactions.js';
import {
  GraphQLCommitment,
  GraphQLPledge,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.js';
import { commitmentsQuery, pledgesQuery } from '@sola/supporter-sdk/queries.js';
import {
  groupCommitmentsByPaymentFrequency,
  sortCommitmentsByPriority,
} from '@sola/supporter-sdk/utils/commitments.js';

import { CommitmentCard } from '../../molecules/commitment-card/commitment-card.js';
import { MakeAPaymentAlert } from '../../molecules/make-a-payment-alert/make-a-payment-alert.js';
import { PledgeCard } from '../../molecules/pledge-card/pledge-card.js';
import { SectionContent } from '../../molecules/section-content/section-content.js';
import {
  CommitmentCardData,
  getCommitmentTotal,
} from './commitment-lists.logic.js';

const getCardElId = (id: string) => `commitment-card-${id}`;

/**
 * Get component data
 */
export const useComponentData = () => {
  const { t } = useTranslation('my');
  const { data, isLoading, isFetching, isPending, isError } = useSupporterQuery(
    [commitmentsQuery, pledgesQuery],
  );
  let commitmentFrequencyGroups: CommitmentCardData[] = [];

  // @ts-expect-error //TODO This should be fixed once we tighten up useSupporterQuery types.
  const [commitments, pledges]: [GraphQLCommitment[], GraphQLPledge[]] = data;

  if (commitments?.length) {
    const {
      [PaymentFrequencyType.Monthly]: monthlyCommitments,
      [PaymentFrequencyType.Quarterly]: quarterlyCommitments,
      [PaymentFrequencyType.Semiannually]: semiannualCommitments,
      [PaymentFrequencyType.Annually]: annualCommitments,
    } = groupCommitmentsByPaymentFrequency(commitments);

    commitmentFrequencyGroups = [
      {
        heading: t('commitments.headings.monthly'),
        frequencyAbbr: t('commitments.frequencyAbbr.monthly'),
        commitments: monthlyCommitments,
      },
      {
        heading: t('commitments.headings.quarterly'),
        frequencyAbbr: t('commitments.frequencyAbbr.quarterly'),
        commitments: quarterlyCommitments,
      },
      {
        heading: t('commitments.headings.semiannually'),
        frequencyAbbr: t('commitments.frequencyAbbr.semiannually'),
        commitments: semiannualCommitments,
      },
      {
        heading: t('commitments.headings.annually'),
        frequencyAbbr: t('commitments.frequencyAbbr.annually'),
        commitments: annualCommitments,
      },
    ];
  }
  return {
    isShowingSkeletons: (isLoading || isFetching || isPending) && !isError,
    commitmentFrequencyGroups,
    hasError: isError,
    pledges,
  };
};

// TODO: Might be worthwhile to extract PledgeList from this component - which would allow for separating it from the Commitment List
type PledgeListProps = {
  pledges: GraphQLPledge[];
};

const PledgeList: React.FC<PledgeListProps> = ({ pledges }) => {
  const { t } = useTranslation(['my']);

  const [activePledgeId, setPledgeIdQuery] = useQueryState('id', {
    history: 'push',
  });
  const setActiveQueryId = (open: boolean, pledgeId: string) => {
    if (open) {
      setPledgeIdQuery(pledgeId);
    } else {
      setPledgeIdQuery(null);
    }
  };

  /**
   * Scroll to active card after page loads.
   */
  useEffect(() => {
    if (!activePledgeId) {
      return;
    }

    const activeCard = document.getElementById(getCardElId(activePledgeId));
    if (!activeCard) {
      return;
    }

    activeCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  return (
    <div key="commitment-group-pledge" className="CommitmentLists__group">
      <SectionContent heading={t('financialPledges')}>
        {pledges.map((pledge) => {
          if (pledge.pledgeId === null) {
            return;
          }

          const id = getCardElId(pledge.pledgeId!);

          return (
            <div id={id} key={id}>
              <PledgeCard
                pledge={pledge}
                onActiveChange={setActiveQueryId}
                isActive={pledge.pledgeId === activePledgeId}
              />
            </div>
          );
        })}
      </SectionContent>
    </div>
  );
};

export type CommitmentListGroupsProps = {
  commitmentFrequencyGroups: CommitmentCardData[];
};

const CommitmentListGroups: React.FC<CommitmentListGroupsProps> = ({
  commitmentFrequencyGroups,
}) => {
  const { t } = useTranslation('common');
  const [activeCommitmentId, setCommitmentIdQuery] = useQueryState('id', {
    history: 'push',
  });
  const setActiveQueryId = (open: boolean, commitmentId: string) => {
    if (open) {
      setCommitmentIdQuery(commitmentId);
    } else {
      setCommitmentIdQuery(null);
    }
  };

  /**
   * Scroll to active card after page loads.
   */
  useEffect(() => {
    if (!activeCommitmentId) {
      return;
    }

    const activeCard = document.getElementById(getCardElId(activeCommitmentId));
    if (!activeCard) {
      return;
    }

    activeCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  return commitmentFrequencyGroups.map(
    ({ heading, frequencyAbbr, commitments: commitmentsByFrequency }) => {
      const commitmentsByType = sortCommitmentsByPriority(
        commitmentsByFrequency,
        t,
      );

      return (
        !!commitmentsByType?.length && (
          <div
            className="CommitmentLists__group"
            key={`commitment-group-${heading}`}
          >
            <SectionContent
              heading={heading}
              subheading={
                <>
                  {t('numbers.currencyCustom', {
                    amount: getCommitmentTotal(commitmentsByFrequency),
                  })}
                  <span className="label-neutral-mild">{frequencyAbbr}</span>
                </>
              }
            >
              {commitmentsByType.map((commitment) => {
                const id = getCardElId(commitment.id);
                return (
                  <div id={id} key={id}>
                    <CommitmentCard
                      commitment={commitment}
                      onActiveChange={setActiveQueryId}
                      isActive={commitment.id === activeCommitmentId}
                    />
                  </div>
                );
              })}
            </SectionContent>
          </div>
        )
      );
    },
  );
};

/**
 * Displays list of commitments based on frequency.
 */
export const CommitmentLists = () => {
  const { isShowingSkeletons, commitmentFrequencyGroups, hasError, pledges } =
    useComponentData();
  const { t } = useTranslation(['my', 'common']);

  return (
    <div className="CommitmentLists max-w-md">
      {isShowingSkeletons && (
        <SectionContent isLoadingContent isLoadingHeading />
      )}

      {/* TODO: Is this the right place for this? */}
      {!isShowingSkeletons && <MakeAPaymentAlert />}

      {!isShowingSkeletons && !!pledges?.length && (
        <PledgeList pledges={pledges} />
      )}

      {!isShowingSkeletons && !!commitmentFrequencyGroups.length && (
        <CommitmentListGroups
          commitmentFrequencyGroups={commitmentFrequencyGroups}
        />
      )}

      {!isShowingSkeletons &&
        !hasError &&
        !commitmentFrequencyGroups.length &&
        !pledges?.length && (
          <>
            <h3 className="heading-sm mb-snug-xl">
              {t('my:commitments.noCommitments.title')}
            </h3>

            <p className="body-sm label-neutral-mild">
              {t('my:commitments.noCommitments.message')}
            </p>

            <p className="flex">
              <ButtonStyle className="" size="md" variant="primary">
                <a
                  href={`${process.env.NEXT_PUBLIC_BASE_URL_DOTCOM}/sponsor-a-child`}
                >
                  {t('commitments.noCommitments.ctaText')}
                </a>
              </ButtonStyle>
            </p>
          </>
        )}

      {!isShowingSkeletons && hasError && (
        <Alert
          variant="negative"
          title={t('commitments.queryError.title')}
          message={t('commitments.queryError.message')}
          action={
            <a href={t('my:ctas.contactSupport.url')}>
              {t('my:ctas.contactSupport.text')}
            </a>
          }
        />
      )}
    </div>
  );
};
